import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "../utils/apiError";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineExclamation,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineBookOpen,
  HiOutlineChevronLeft,
  HiOutlineSearch,
} from "react-icons/hi";
import {
  fetchCompetencyHeaders,
  createCompetencyHeader,
  updateCompetencyHeader,
  deleteCompetencyHeader,
  fetchCompetencySubHeaders,
  createCompetencySubHeader,
  updateCompetencySubHeader,
  deleteCompetencySubHeader,
  fetchCompetencyHeaderDetail,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import { useUserRole } from "../hooks/useUserRole";
import { useDebounce } from "../hooks/useDebounce";

const hasLetter = (v) => /\p{L}/u.test(v.trim());
const nameLetterError = "Name must contain at least one letter (not just numbers, spaces, or symbols)";

/* ═══════════════════════════════════════════════════════════════════
   COMPETENCY HEADER & SUBHEADER PAGE
   ═══════════════════════════════════════════════════════════════════ */
function CompetencyHeader() {
  const { canManageCompetencies } = useUserRole();
  const [toast, setToast] = useState(null);

  // ── view: "list" | "form" — backed by the URL so a refresh in edit mode
  //    reopens the same header instead of dropping back to the list ──
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "list";
  const urlEditId = searchParams.get("edit");
  const setView = useCallback((v, params = {}) => {
    if (v === "list") {
      setSearchParams({});
    } else if (v === "form" && params.edit) {
      setSearchParams({ view: "form", edit: params.edit });
    } else {
      setSearchParams({ view: v });
    }
  }, [setSearchParams]);

  // ── headers list ──
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // ── pagination state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ── header form (in-page) ──
  const [headerForm, setHeaderForm] = useState({ name: "" });
  const [editingHeaderId, setEditingHeaderId] = useState(null);
  const [savingHeader, setSavingHeader] = useState(false);

  // ── inline sub-headers for create form ──
  const [pendingSubs, setPendingSubs] = useState([]);
  const [pendingSubInput, setPendingSubInput] = useState("");

  // ── delete confirm ──
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── sub-headers data ──
  const [subHeadersMap, setSubHeadersMap] = useState({});
  const [loadingSubs, setLoadingSubs] = useState({});

  // ── inline add sub-header ──
  const [addingSubFor, setAddingSubFor] = useState(null);
  const [newSubName, setNewSubName] = useState("");
  const [savingNewSub, setSavingNewSub] = useState(false);

  // ── inline edit sub-header ──
  const [editingSubId, setEditingSubId] = useState(null);
  const [editSubName, setEditSubName] = useState("");
  const [savingEditSub, setSavingEditSub] = useState(false);

  /* ── load headers ──────────────────────────────────────────────── */

  const loadHeaders = useCallback(async (page = currentPage, searchQuery = debouncedSearch) => {
    setLoading(true);
    try {
      const res = await fetchCompetencyHeaders({ page, page_size: pageSize, search: searchQuery });
      setHeaders(res.data || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
      setCurrentPage(res.current_page || page);
    } catch (err) {
      console.error("Failed to load headers", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    loadHeaders(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, debouncedSearch]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  /* ── load subheaders for a header ─────────────────────────────── */

  const loadSubHeaders = useCallback(async (headerId) => {
    setLoadingSubs((prev) => ({ ...prev, [headerId]: true }));
    try {
      const data = await fetchCompetencySubHeaders(headerId);
      setSubHeadersMap((prev) => ({ ...prev, [headerId]: data }));
    } catch (err) {
      console.error("Failed to load sub-headers", err);
    } finally {
      setLoadingSubs((prev) => ({ ...prev, [headerId]: false }));
    }
  }, []);

  /* ── header form helpers ───────────────────────────────────────── */

  const openCreateHeader = () => {
    setHeaderForm({ name: "" });
    setEditingHeaderId(null);
    setPendingSubs([]);
    setPendingSubInput("");
    setView("form");
  };

  const openEditHeader = (h) => {
    // A direct click already has the full item — mark the URL-restore effect
    // as satisfied so it doesn't also redundantly re-fetch this same item.
    hasRestoredFromUrl.current = true;
    setHeaderForm({ name: h.name });
    setEditingHeaderId(h.id);
    setPendingSubs([]);
    setPendingSubInput("");
    setView("form", { edit: h.id });
    if (!h._subsLoaded && !subHeadersMap[h.id]) loadSubHeaders(h.id);
  };

  // Restore edit view from URL on refresh — fetch the full header detail
  // (bare object; the endpoint has no {success,data} envelope) and feed it
  // through the normal openEditHeader path.
  const hasRestoredFromUrl = useRef(false);
  useEffect(() => {
    if (hasRestoredFromUrl.current) return;
    if (view === "form" && urlEditId) {
      hasRestoredFromUrl.current = true;
      fetchCompetencyHeaderDetail(urlEditId)
        .then((h) => {
          // The detail already includes sub_headers — seed the map so
          // openEditHeader doesn't fetch the same list a second time.
          if (Array.isArray(h.sub_headers)) {
            setSubHeadersMap((prev) => ({ ...prev, [h.id]: h.sub_headers }));
          }
          openEditHeader({ ...h, _subsLoaded: Array.isArray(h.sub_headers) });
        })
        .catch(() => setView("list"));
    } else if (view === "form" && !urlEditId) {
      hasRestoredFromUrl.current = true;
      openCreateHeader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, urlEditId]);

  const closeForm = () => {
    setView("list");
    setHeaderForm({ name: "" });
    setEditingHeaderId(null);
    setPendingSubs([]);
    setPendingSubInput("");
  };

  const addPendingSub = () => {
    const val = pendingSubInput.trim();
    if (!val) return;
    if (pendingSubs.includes(val)) {
      setToast({ type: "error", message: "Sub-header already added" });
      return;
    }
    setPendingSubs([...pendingSubs, val]);
    setPendingSubInput("");
  };

  const removePendingSub = (idx) => {
    setPendingSubs(pendingSubs.filter((_, i) => i !== idx));
  };

  const handleSaveHeader = async (e) => {
    e.preventDefault();
    if (!headerForm.name.trim()) {
      setToast({ type: "error", message: "Header name is required" });
      return;
    }
    if (!hasLetter(headerForm.name) || pendingSubs.some((s) => !hasLetter(s))) {
      setToast({ type: "error", message: nameLetterError });
      return;
    }
    setSavingHeader(true);
    try {
      if (editingHeaderId) {
        await updateCompetencyHeader(editingHeaderId, { name: headerForm.name.trim() });
        // Create any new pending sub-headers
        for (const subName of pendingSubs) {
          await createCompetencySubHeader({ name: subName, header_id: editingHeaderId });
        }
        setToast({ type: "success", message: "Header updated" });
        loadSubHeaders(editingHeaderId);
      } else {
        await createCompetencyHeader({
          name: headerForm.name.trim(),
          sub_headers: pendingSubs,
        });
        setToast({ type: "success", message: "Header created" });
      }
      closeForm();
      loadHeaders();
    } catch (err) {
      setToast({
        type: "error",
        message: apiErrorMessage(err, "Failed to save header"),
      });
    } finally {
      setSavingHeader(false);
    }
  };

  /* ── inline add sub-header ──────────────────────────────────── */

  const startAddSub = (headerId) => {
    setAddingSubFor(headerId);
    setNewSubName("");
  };

  const cancelAddSub = () => {
    setAddingSubFor(null);
    setNewSubName("");
  };

  const saveNewSub = async (headerId) => {
    if (!newSubName.trim()) return;
    if (!hasLetter(newSubName)) {
      setToast({ type: "error", message: nameLetterError });
      return;
    }
    setSavingNewSub(true);
    try {
      await createCompetencySubHeader({ name: newSubName.trim(), header_id: headerId });
      setToast({ type: "success", message: "Sub-header created" });
      setAddingSubFor(null);
      setNewSubName("");
      loadSubHeaders(headerId);
      loadHeaders();
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, "Failed to create sub-header") });
    } finally {
      setSavingNewSub(false);
    }
  };

  /* ── inline edit sub-header ────────────────────────────────── */

  const startEditSub = (sub) => {
    setEditingSubId(sub.id);
    setEditSubName(sub.name);
  };

  const cancelEditSub = () => {
    setEditingSubId(null);
    setEditSubName("");
  };

  const saveEditSub = async (subId, headerId) => {
    if (!editSubName.trim()) return;
    if (!hasLetter(editSubName)) {
      setToast({ type: "error", message: nameLetterError });
      return;
    }
    setSavingEditSub(true);
    try {
      await updateCompetencySubHeader(subId, { name: editSubName.trim() });
      setToast({ type: "success", message: "Sub-header updated" });
      setEditingSubId(null);
      loadSubHeaders(headerId);
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, "Failed to update sub-header") });
    } finally {
      setSavingEditSub(false);
    }
  };

  /* ── delete helpers ────────────────────────────────────────────── */

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "header") {
        await deleteCompetencyHeader(deleteTarget.item.id);
        setToast({ type: "success", message: "Header deleted" });
        loadHeaders();
        if (view !== "list") setView("list");
      } else {
        await deleteCompetencySubHeader(deleteTarget.item.id);
        setToast({ type: "success", message: "Sub-header deleted" });
        if (deleteTarget.headerId) {
          loadSubHeaders(deleteTarget.headerId);
          loadHeaders();
        }
      }
      setDeleteTarget(null);
    } catch (err) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || "Delete failed",
      });
    }
  };

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */

  const inputClass = "w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10";
  const btnPrimary = "py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]";
  const btnSecondary = "py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]";

  const formSubs = editingHeaderId ? (subHeadersMap[editingHeaderId] || []) : [];
  const formSubsLoading = editingHeaderId ? !!loadingSubs[editingHeaderId] : false;

  return (
    <div className="p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ═══ FORM VIEW ═══ */}
      {view === "form" && (
        <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg border border-[#e5e7eb]">
          {/* Form Header */}
          <div className="sticky top-14 z-40 flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb] bg-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <button onClick={closeForm} className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer">
                <HiOutlineChevronLeft className="text-lg" />
              </button>
              <div>
                <h2 className="m-0 text-[15px] font-semibold text-[#24292f]">
                  {!canManageCompetencies ? "Header Details" : editingHeaderId ? "Edit Header" : "Add New Header"}
                </h2>
                <p className="m-0 text-[13px] text-[#8b949e]">
                  {!canManageCompetencies ? "View header information" : editingHeaderId ? "Update header and manage sub-headers" : "Create a new competency header with sub-headers"}
                </p>
              </div>
            </div>
            {canManageCompetencies && editingHeaderId && (
              <button
                type="button"
                onClick={() => setDeleteTarget({ type: "header", item: { id: editingHeaderId, name: headerForm.name, subheader_count: formSubs.length } })}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>

          <form onSubmit={handleSaveHeader}>
            <div className="p-6 space-y-5">
              {/* Header Name fieldset */}
              <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
                <legend className="text-xs font-semibold text-[#57606a] px-2">Header Info</legend>
                <div>
                  <label htmlFor="header-name" className="block mb-2 text-sm font-medium text-[#24292f]">Header Name <span className="text-red-500">*</span></label>
                  <input id="header-name" className={inputClass} type="text" placeholder="Enter header name"
                    value={headerForm.name} onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })} required autoFocus
                    disabled={!canManageCompetencies}
                  />
                </div>
              </fieldset>

              {/* Sub-headers fieldset */}
              <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
                <legend className="text-xs font-semibold text-[#57606a] px-2">
                  Sub-Headers {editingHeaderId && !formSubsLoading ? `(${formSubs.length})` : ""}
                </legend>

                {/* Existing sub-headers (when editing) */}
                {editingHeaderId && (
                  <div className="mb-3">
                    {formSubsLoading ? (
                      <div className="space-y-2">
                        {[1, 2].map((i) => (
                          <div key={i} className="flex items-center gap-3 animate-pulse">
                            <div className="w-6 h-4 bg-[#e5e7eb] rounded" />
                            <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
                          </div>
                        ))}
                      </div>
                    ) : formSubs.length === 0 ? (
                      <p className="text-xs text-[#8b949e] mb-2">No existing sub-headers.</p>
                    ) : (
                      <table className="w-full text-sm mb-2">
                        <tbody className="divide-y divide-[#f0f0f0]">
                          {formSubs.map((sub, idx) => (
                            <tr key={sub.id} className="group hover:bg-[#f6f8fa] transition-colors">
                              <td className="py-2 px-2 text-[#8b949e] text-xs w-[40px]">{idx + 1}</td>
                              <td className="py-2 px-2">
                                {editingSubId === sub.id ? (
                                  <input className={inputClass + " max-w-xs"} value={editSubName}
                                    onChange={(e) => setEditSubName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveEditSub(sub.id, editingHeaderId); if (e.key === "Escape") cancelEditSub(); }}
                                    autoFocus disabled={savingEditSub}
                                  />
                                ) : (
                                  <span className="text-[#24292f] font-medium">{sub.name}</span>
                                )}
                              </td>
                              {canManageCompetencies && (
                                <td className="py-2 px-2 w-[100px]">
                                  <div className="flex justify-end gap-1">
                                    {editingSubId === sub.id ? (
                                      <>
                                        <button type="button" className="w-7 h-7 flex items-center justify-center rounded-md text-green-600 hover:bg-green-50 transition-all border-none bg-transparent cursor-pointer disabled:opacity-40"
                                          onClick={() => saveEditSub(sub.id, editingHeaderId)} disabled={savingEditSub || !editSubName.trim()}>
                                          <HiOutlineCheck className="text-sm" />
                                        </button>
                                        <button type="button" className="w-7 h-7 flex items-center justify-center rounded-md text-[#8b949e] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
                                          onClick={cancelEditSub} disabled={savingEditSub}>
                                          <HiOutlineX className="text-sm" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button type="button" className="w-7 h-7 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#0078d4] hover:bg-[#eff6ff] transition-all border-none bg-transparent cursor-pointer"
                                          onClick={() => startEditSub(sub)}>
                                          <HiOutlinePencil className="text-xs" />
                                        </button>
                                        <button type="button" className="w-7 h-7 flex items-center justify-center rounded-md text-[#8b949e] hover:text-red-600 hover:bg-red-50 transition-all border-none bg-transparent cursor-pointer"
                                          onClick={() => setDeleteTarget({ type: "subheader", item: sub, headerId: editingHeaderId })}>
                                          <HiOutlineTrash className="text-xs" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Inline add sub-header for edit mode */}
                    {addingSubFor === editingHeaderId ? (
                      <div className="flex items-center gap-2 mt-2">
                        <input className={inputClass + " flex-1"} placeholder="New sub-header name" value={newSubName}
                          onChange={(e) => setNewSubName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNewSub(editingHeaderId); } if (e.key === "Escape") cancelAddSub(); }}
                          autoFocus disabled={savingNewSub}
                        />
                        <button type="button" className={btnPrimary + " text-xs !py-2 disabled:opacity-40"}
                          onClick={() => saveNewSub(editingHeaderId)} disabled={savingNewSub || !newSubName.trim()}>
                          {savingNewSub ? "Adding…" : "Add"}
                        </button>
                        <button type="button" className={btnSecondary + " text-xs !py-2"} onClick={cancelAddSub}>Cancel</button>
                      </div>
                    ) : canManageCompetencies && (
                      <button type="button" onClick={() => startAddSub(editingHeaderId)} className="text-xs text-[#0078d4] hover:underline flex items-center gap-1 mt-1">
                        <HiOutlinePlus className="text-sm" /> Add Sub-Header
                      </button>
                    )}
                  </div>
                )}

                {/* Pending sub-headers (for create mode) */}
                {!editingHeaderId && (
                  <div>
                    <div className="flex gap-2 mb-2">
                      <input className={inputClass + " flex-1"} type="text" placeholder="Enter sub-header name"
                        value={pendingSubInput} onChange={(e) => setPendingSubInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPendingSub(); } }}
                      />
                      <button type="button" onClick={addPendingSub} disabled={!pendingSubInput.trim()}
                        className={btnPrimary + " text-xs !py-2 disabled:opacity-40"}>
                        <HiOutlinePlus className="text-sm inline mr-1" /> Add
                      </button>
                    </div>
                    {pendingSubs.length > 0 && (
                      <div className="space-y-1.5">
                        {pendingSubs.map((sub, idx) => (
                          <div key={idx} className="flex items-center justify-between px-3 py-2 bg-[#f6f8fa] rounded-md border border-[#e5e7eb]">
                            <span className="text-sm text-[#24292f]">{sub}</span>
                            <button type="button" onClick={() => removePendingSub(idx)}
                              className="w-6 h-6 flex items-center justify-center rounded text-[#8b949e] hover:text-red-600 hover:bg-red-50 transition-all border-none bg-transparent cursor-pointer">
                              <HiOutlineX className="text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {pendingSubs.length === 0 && (
                      <p className="m-0 text-xs text-[#8b949e]">Type a name and click Add. You can add multiple before saving.</p>
                    )}
                  </div>
                )}
              </fieldset>

              {/* Form actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className={btnSecondary} onClick={closeForm}>
                  {canManageCompetencies ? "Cancel" : "Back"}
                </button>
                {canManageCompetencies && (
                  <button type="submit" disabled={savingHeader || !headerForm.name.trim()} className={btnPrimary + " disabled:opacity-50"}>
                    {savingHeader ? "Saving…" : editingHeaderId ? "Save Changes" : "Create Header"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {view === "list" && (
      <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
        {/* Toolbar (title/subtitle come from the shared page Header) */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
          <div className="relative flex items-center flex-1 max-w-sm">
            <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
            <input
              type="text"
              className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
              placeholder="Search headers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
                onClick={() => setSearch("")}
              >
                <HiOutlineX className="text-sm" />
              </button>
            )}
          </div>
          {canManageCompetencies && (
            <button className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]" onClick={openCreateHeader}>
              <HiOutlinePlus className="text-base" /> Add Header
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-5 h-5 bg-[#e5e7eb] rounded" />
                <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
                <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
              </div>
            ))}
          </div>
        ) : headers.length === 0 ? (
          <div className="text-center py-16 px-5">
            <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
              <HiOutlineBookOpen className="text-2xl text-[#8b949e]" />
            </div>
            <p className="m-0 text-sm font-medium text-[#57606a]">No headers found.</p>
            <p className="m-0 text-xs text-[#8b949e] mt-1">Create your first competency header to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[50px]">#</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a]">Header Name</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[120px]">Sub-Headers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e7eb]">
              {headers.map((h, idx) => (
                <tr key={h.id} className="group hover:bg-gray-50 transition-colors duration-150 cursor-pointer" onClick={() => openEditHeader(h)}>
                  <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{idx + 1}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#eff6ff] flex-shrink-0">
                        <HiOutlineBookOpen className="text-xs text-[#0078d4]" />
                      </div>
                      <span className="font-medium text-[#24292f]">{h.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#f6f8fa] text-[#57606a] rounded font-medium">
                      {h.subheader_count} subheader{h.subheader_count === 1 ? "" : "s"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={(p) => loadHeaders(p)}
            onPageSizeChange={handlePageSizeChange}
            entityName="headers"
          />
        )}
      </div>
      )}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") setDeleteTarget(null);
            }}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamation className="text-2xl text-red-600" />
              </div>
              <h3 className="m-0 text-lg font-semibold text-[#24292f]">
                Delete {deleteTarget.type === "header" ? "Header" : "Sub-header"}?
              </h3>
              <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
                Are you sure you want to delete {deleteTarget.item.name}?
                {deleteTarget.type === "header" && deleteTarget.item.subheader_count > 0 && (
                  <span className="block mt-1 text-red-600 text-xs font-medium">
                    This will also delete {deleteTarget.item.subheader_count} sub-header
                    {deleteTarget.item.subheader_count !== 1 ? "s" : ""}.
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm"
                onClick={handleDelete}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompetencyHeader;
